import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';

export const nutritionTableColumns = (
  handleDelete: (index: number) => void
): ColumnDef<any>[] => [
  {
    accessorKey: 'name',
    header: 'Food',
    cell: ({ row: { original } }) => {
      const { name } = original;
      return <>{name}</>;
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-3 pe-3 border-start border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-3 border-start border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'portion',
    header: 'Portion',
    cell: ({ row: { original } }) => {
      const { quantity, servingSizeUnit } = original;
      return (
        <>
          {quantity}&nbsp;{'g'}
        </>
      );
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-3 pe-3 border-start border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-3 border-start border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'Carbohydrate',
    header: 'Carbohydrate',
    cell: ({ row: { original } }) => {
      const nutritionFact = original?.nutritionFact;
      const { quantity } = original;
      const res = nutritionFact?.find(
        (item: any) => item.nutrientName == 'Carbohydrate, by difference'
      );
      return (
        <>
          {res
            ? ((res.value * quantity) / 100).toFixed(2) + ' ' + res.unitName
            : '-'}
        </>
      );
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-3 pe-3 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-3 border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'Protein',
    header: 'Protein',
    cell: ({ row: { original } }) => {
      const nutritionFact = original?.nutritionFact;
      const { quantity } = original;
      const res = nutritionFact?.find(
        (item: any) => item.nutrientName == 'Protein'
      );
      return (
        <>
          {res
            ? ((res.value * quantity) / 100).toFixed(2) + ' ' + res.unitName
            : '-'}
        </>
      );
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-3 pe-3 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-3 border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'Sugars',
    header: 'Sugars',
    cell: ({ row: { original } }) => {
      const nutritionFact = original?.nutritionFact;
      const { quantity } = original;
      const res = nutritionFact?.find(
        (item: any) => item.nutrientName == 'Total Sugars'
      );
      return (
        <>
          {res
            ? ((res.value * quantity) / 100).toFixed(2) + ' ' + res.unitName
            : '-'}
        </>
      );
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-3 pe-3 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-3 border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'Total lipid (fat)',
    header: 'Total (fat)',
    cell: ({ row: { original } }) => {
      const nutritionFact = original?.nutritionFact;
      const { quantity } = original;
      const res = nutritionFact?.find(
        (item: any) => item.nutrientName == 'Total lipid (fat)'
      );
      return (
        <>
          {res
            ? ((res.value * quantity) / 100).toFixed(2) + ' ' + res.unitName
            : '-'}
        </>
      );
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-3 pe-3 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-3 border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'Energy',
    header: 'Energy',
    cell: ({ row: { original } }) => {
      const nutritionFact = original?.nutritionFact;
      const { quantity } = original;
      const res = nutritionFact?.find(
        (item: any) => item.nutrientName == 'Energy' && item.unitName === 'kcal'
      );
      return (
        <>
          {res
            ? ((res.value * quantity) / 100).toFixed(2) + ' ' + res.unitName
            : '-'}
        </>
      );
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-3 pe-3 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-3 border-end border-translucent'
      }
    }
  },
  {
    accessorKey: 'updatedByName',
    header: 'Updated By',
    cell: ({ row: { original } }) => {
      if (!original.updatedByName) {
        return null; // render empty cell
      }

      const name = original.updatedByName;
      const date = original.updatedAt;

      const formattedDate = new Date(date).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      return (
        <div>
          <div>{name}</div>
          <div className="text-muted fs-10">{formattedDate}</div>
        </div>
      );
    },
    meta: {
      headerProps: {
        style: { width: 'auto' },
        className: 'ps-3 pe-3 border-end border-translucent'
      },
      cellProps: {
        className:
          'white-space-nowrap fw-semibold ps-3 border-end border-translucent'
      }
    }
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      return (
        <button
          onClick={() => handleDelete(row.index)}
          className="btn btn-sm btn-danger"
        >
          Delete
        </button>
      );
    },
    meta: {
      headerProps: {
        className: 'ps-2 pe-3 border-end border-translucent'
      },
      cellProps: {
        className: 'ps-2 border-end border-translucent'
      }
    }
  }
];

const NutritionTable = () => {
  return (
    <div className="border-top border-translucent">
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger"
      />
    </div>
  );
};

export default NutritionTable;
